/**
 * RBAC permission matrix — the single source of truth for both apps.
 * The API enforces it in middleware; the web app uses it to hide affordances.
 * Hiding UI is cosmetic only: every check that matters happens server-side.
 */

export const ROLES = ['admin', 'staff'] as const
export type Role = (typeof ROLES)[number]

export const PERMISSIONS = [
  // dashboard & alerts
  'dashboard:view',
  'alerts:view',

  // clients
  'clients:view',
  'clients:create',
  'clients:update',
  'clients:delete',
  'clients:bulk',
  'clients:review',
  'clients:export',

  // registration links
  'links:view',
  'links:create',
  'links:update',
  'links:delete',

  // messaging
  'templates:view',
  'templates:manage',
  'campaigns:view',
  'campaigns:send',

  // administration
  'users:view',
  'users:manage',
  'settings:view',
  'settings:manage',
  'audit:view',
] as const

export type Permission = (typeof PERMISSIONS)[number]

/**
 * Staff deliberately lack: hard deletes, template authoring, user management,
 * settings *writes* and the audit log. See PLAN.md §3.
 *
 * Deviation from the §3 matrix, deliberate: staff get `settings:view` (read
 * only). The review screen has to be able to say "confirming will send a
 * welcome message" — bulk-confirming 50 registrants and silently sending 50
 * paid SMS is exactly the surprise the matrix was never trying to protect.
 * Nothing secret lives in `settings`; provider keys are Worker secrets
 * (PLAN §7.1), so reading it discloses nothing. `settings:manage` stays admin.
 */
const STAFF_PERMISSIONS: readonly Permission[] = [
  'dashboard:view',
  'alerts:view',
  'clients:view',
  'clients:create',
  'clients:update',
  'clients:bulk',
  'clients:review',
  'clients:export',
  'links:view',
  'links:create',
  'links:update',
  'templates:view',
  'campaigns:view',
  'campaigns:send',
  'settings:view',
]

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  admin: PERMISSIONS,
  staff: STAFF_PERMISSIONS,
}

export function hasPermission(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission)
}

export function hasAnyPermission(role: Role, permissions: readonly Permission[]): boolean {
  return permissions.some((p) => hasPermission(role, p))
}
